/**
 * Vocabulary Hydration
 * --------------------
 * Mutates the canonical design-system maps in place from DB vocabulary rows.
 *
 * Why in-place mutation?
 *   Object.freeze() in JS is SHALLOW — the inner objects inside a frozen map
 *   remain mutable. This lets us update colours/labels without re-creating
 *   objects, which means every consumer that holds an existing reference
 *   (e.g. `PRIORITY_V3['-1']`) automatically sees the new values on the
 *   next render (forced by VocabularyProvider's version-keyed remount).
 *
 * Maps hydrated:
 *   - PRIORITY_V3            (taskIdentity.js)  ← primary priority source
 *   - STATE_V3               (taskIdentity.js)  ← primary state source
 *   - INTENT_V3              (taskIdentity.js)  ← primary intent source
 *   - TASK_STATE_META        (states.js)        ← extended state map (8 keys)
 *   - EFFORT_OPTIONS         (taskConstants.js) ← per-task effort scale
 *   - PRIORITY_META (derived)(taskConstants.js) ← derived view, re-hydrate
 *   - STATUS_META   (derived)(taskConstants.js) ← derived view, re-hydrate
 */

import {
  PRIORITY_V3,
  STATE_V3,
  INTENT_V3,
  hydratePriorityV3,
  hydrateStateV3,
  hydrateIntentV3,
} from '../design-system/taskIdentity';
import { TASK_STATE_META } from '../design-system/states';
import { PRIORITY_META as LEGACY_PRIORITY_META, STATUS_META, EFFORT_OPTIONS } from '../lib/taskConstants';

// --- Core hydrators (delegated to the design-system helpers) ----------------

function _hydrateTaskStateMeta(rows) {
  if (!Array.isArray(rows)) return;
  rows.forEach((r) => {
    const m = TASK_STATE_META[r.key];
    if (!m) return;
    m.label = r.label || m.label;
    m.short = r.short || m.short;
    m.color = r.resolved_color || r.color || m.color;
  });
}

function _hydrateEffortOptions(rows) {
  if (!Array.isArray(rows)) return;
  rows.forEach((r) => {
    const valueNum = Number(r.key);
    const existing = EFFORT_OPTIONS.find((e) => e.value === valueNum);
    if (!existing) return;
    if (typeof r.minutes === 'number') existing.minutes = r.minutes;
    if (r.short_label) existing.short_label = r.short_label;
    if (r.long_label) existing.long_label = r.long_label;
  });
}

// --- Derived views re-sync (taskConstants.js) --------------------------------
// PRIORITY_META and STATUS_META in taskConstants.js were built at module load
// from PRIORITY_V3 / STATE_V3. After mutating the primary maps we must also
// update these derived copies so legacy consumers (bg/text chip styling) see
// the new colours.

function _pickTextColor(hex) {
  if (!hex || typeof hex !== 'string') return '#FFFFFF';
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#FFFFFF';
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#111827' : '#FFFFFF';
}

function _resyncDerivedPriorityMeta() {
  Object.entries(PRIORITY_V3).forEach(([key, src]) => {
    const dst = LEGACY_PRIORITY_META[key];
    if (!dst) return;
    dst.bg = src.color;
    dst.text = _pickTextColor(src.color);
    dst.label = src.label;
    dst.long = src.long;
  });
}

const _LEGACY_STATUS_TO_STATE = {
  'To Do':       'open',
  'In Progress': 'inProgress',
  'Partial':     'partial',
  'Blocked':     'blocked',
  'Done':        'done',
};
function _resyncDerivedStatusMeta() {
  Object.entries(_LEGACY_STATUS_TO_STATE).forEach(([legacy, stateKey]) => {
    const src = STATE_V3[stateKey];
    const dst = STATUS_META[legacy];
    if (!src || !dst) return;
    dst.bg = src.color;
    dst.text = _pickTextColor(src.color);
    dst.label = src.label;
    dst.short = src.short;
  });
}

// --- Public API --------------------------------------------------------------

export function hydrateFromVocabulary(payload) {
  if (!payload || typeof payload !== 'object') return;
  if (payload.intent)   hydrateIntentV3(payload.intent);
  if (payload.priority) hydratePriorityV3(payload.priority);
  if (payload.status) {
    hydrateStateV3(payload.status);
    _hydrateTaskStateMeta(payload.status);
  }
  if (payload.effort)   _hydrateEffortOptions(payload.effort);
  // Keep derived views in sync.
  _resyncDerivedPriorityMeta();
  _resyncDerivedStatusMeta();
}

// Convenience: a single getter that consumers can use to read current state
// (post-hydration) without importing every canonical map individually.
export function getCurrentVocabulary() {
  return {
    priority: PRIORITY_V3,
    state: STATE_V3,
    intent: INTENT_V3,
    taskStateMeta: TASK_STATE_META,
    effortOptions: EFFORT_OPTIONS,
  };
}
